import React, { useState, useRef, useEffect, useContext } from "react";
import { icons } from "../../../constants";
import { capitalizeFirstWord } from "../../../utils/capitalizeFirstWord";
import { TranslationsContext } from "../../../utils/TranslationsContext";

const Dropdown = ({
  label,
  selectedOption,
  setSelectedOption,
  options,
  className,
  searchable = false,
  variant,
  disabled,
  flags,
  error,
  required,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState(selectedOption || "");
  // Only filter once the user actually types — the input is prefilled with
  // the selected option's text, which would otherwise hide every other
  // option as soon as the menu opens.
  const [isTyping, setIsTyping] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(() => {
    if (selectedOption) {
      const index = options.findIndex((opt) => opt.value === selectedOption);
      return index !== -1 ? index : 0;
    }
    return 0;
  });

  useEffect(() => {
    if (selectedOption) {
      const index = options.findIndex((opt) => opt.value === selectedOption);
      setHighlightedIndex(index !== -1 ? index : 0);
    } else {
      setHighlightedIndex(0);
    }
  }, [selectedOption, options]);

  const containerRef = useRef(null);
  const optionsRef = useRef([]);
  const inputRef = useRef(null);
  const { t } = useContext(TranslationsContext);
  const [theme, setTheme] = useState(
    () => localStorage.getItem("theme") || "light",
  );

  const handleOptionClick = (option) => {
    setSelectedOption(option);
    setSearchValue(options.find((opt) => opt.key === option)?.value);
    setIsTyping(false);
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    setSearchValue(e.target.value);
    setIsTyping(true);
    setIsOpen(true);
    setHighlightedIndex(0);
  };

  const handleBlur = (e) => {
    if (!containerRef.current.contains(e.relatedTarget)) {
      setIsOpen(false);
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < visibleOptions.length - 1 ? prev + 1 : 0,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : visibleOptions.length - 1,
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      const selected = visibleOptions[highlightedIndex];
      if (selected) {
        handleOptionClick(selected.key);
      }
    }
  };

  const visibleOptions = searchable && isTyping
    ? options.filter((option) =>
        String(option?.value ?? "")
          .toLowerCase()
          .includes(String(searchValue ?? "").toLowerCase()),
      )
    : options;

  useEffect(() => {
    if (isOpen && optionsRef.current[highlightedIndex]) {
      optionsRef.current[highlightedIndex].scrollIntoView({
        block: "nearest",
      });
    }
  }, [highlightedIndex, isOpen]);

  return (
    <div
      ref={containerRef}
      className={`flex-direction-column items-center gap-8 width-100 height-100 dropdown ${
        variant === "send-all" ? "send-all" : ""
      } ${className}`}
    >
      {label && (
        <div className="flex-direction-row items-center width-100 relative">
          {required && (
            <img
              src={icons.required}
              alt="req"
              width={12}
              height={12}
              style={{ position: "absolute", top: "0px", left: "0" }}
            />
          )}
          <label className="font-12 inria-sans-700">{label}</label>
        </div>
      )}
      <div
        className={`flex-direction-row items-center gap-8 width-100 dropdown-input ${
          disabled ? "disabled" : ""
        } ${error ? "error" : ""}`}
      >
        {selectedOption && flags && (
          <img
            src={options.find((opt) => opt.value === selectedOption)?.flag}
            alt="flag"
            className="dropdown-flag"
            width={24}
            height={24}
          />
        )}
        <input
          ref={inputRef}
          value={
            searchable
              ? searchValue
              : selectedOption
                ? capitalizeFirstWord(String(selectedOption))
                : ""
          }
          disabled={disabled}
          className={`font-12 inria-sans-400 width-100 ${
            variant === "send-all" ? "weight-700" : ""
          }`}
          onChange={searchable ? handleInputChange : undefined}
          readOnly={!searchable}
          placeholder={searchable ? "Search..." : ""}
          onFocus={() => {
            setIsTyping(false);
            setIsOpen(true);
            if (searchable) {
              setTimeout(() => {
                inputRef.current?.select();
              }, 100);
            }
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
        />
        {!disabled && (
          <img
            src={theme === "light" ? icons.arrow_down : icons.arrow_down_dark}
            alt="arrow-down"
            className="pointer"
            onClick={(e) => {
              e.stopPropagation();

              if (isOpen) {
                setIsOpen(false);
                inputRef.current?.blur();
              } else {
                inputRef.current?.focus();
              }
            }}
            width={28}
            height={28}
            tabIndex={0}
            onBlur={handleBlur}
            style={{
              transform: !isOpen ? "rotate(0)" : "rotate(180deg)",
              transition: "transform 0.1s ease",
            }}
          />
        )}
      </div>
      {isOpen && (
        <div className="flex-direction-column width-100 dropdown-menu">
          {visibleOptions.length > 0 ? (
            visibleOptions.map((option, index) => (
              <div
                key={option.key}
                ref={(el) => (optionsRef.current[index] = el)}
                className={`flex-direction-row gap-8 items-center inria-sans-300 font-12 pointer dropdown-option ${
                  index === highlightedIndex ? "highlighted" : ""
                } ${variant === "send-all" ? "weight-700" : ""}`}
                onClick={() => handleOptionClick(option.key)}
                tabIndex={0}
                onBlur={handleBlur}
              >
                {option.flag && <img src={option.flag} alt="flag" />}
                {/* Optional rich markup for the menu row; search and the
                    selected-value text still use the plain `value` string. */}
                {option.render ?? option.value}
              </div>
            ))
          ) : (
            <div className="inria-sans-300 font-12 dropdown-option">
              {t("NO_OPTIONS_FOUND")}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
